import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Divider,
} from '@mui/material';
import { useTranslate, useLocaleState } from 'react-admin';
import {
  buildSaleReportSectionOptions,
  exportSaleReportExcel,
} from '../../utils/exportSaleReportExcel';
import { formatDateValue } from '../../utils/dateUtils';

/**
 * Sale Report — bölüm seç + stilli Excel çıktı.
 */
const SaleReportExportDialog = ({
  open,
  onClose,
  event,
  stock,
  salesGrid,
  couponsGrid,
  remainingGrid,
  ownershipBreakdown,
  totals,
  onNotify,
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const options = useMemo(
    () => buildSaleReportSectionOptions(ownershipBreakdown),
    [ownershipBreakdown],
  );

  const [selected, setSelected] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(options.map((o) => o.id));
  }, [open, options]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAll = () => setSelected(options.map((o) => o.id));
  const clearAll = () => setSelected([]);

  const selectedLabels = options
    .filter((o) => selected.includes(o.id))
    .map((o) => o.label);

  const handleExport = () => {
    if (selected.length === 0) {
      onNotify?.('En az bir bölüm seç.', { type: 'warning' });
      return;
    }
    setExporting(true);
    try {
      const venueOwner = (ownershipBreakdown?.owners || []).find((o) => o.type === 'venue');
      const fileName = exportSaleReportExcel({
        event,
        stock,
        selectedIds: selected,
        selectedLabels,
        salesGrid,
        couponsGrid,
        remainingGrid,
        ownershipBreakdown,
        totals,
        venueName: venueOwner?.name,
      });
      onNotify?.(`Excel indirildi: ${fileName}`, { type: 'success' });
      onClose();
    } catch (err) {
      onNotify?.(err?.message || 'Excel çıktısı alınamadı.', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={exporting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('report.export_report_excel', { _: 'Rapor Al — Excel' })}</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: '#f5f7fb',
            border: '1px solid #e3e8f0',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {event?.name || 'Etkinlik'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tarih: {formatDateValue(event?.startdate, 'LL', locale) || '—'}
            {stock?.time ? `  ·  Seans: ${stock.time}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Event ID: {event?.id || event?.uid || '—'}
            {stock?.id ? `  ·  Stock: ${stock.id}` : ''}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate('report.export_desc', {
            _: 'Excel’e girecek bölümleri seç. Her seçim ayrı sheet; kapak sayfasında etkinlik bilgisi ve seçilen bölümler yazar.',
          })}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" onClick={selectAll}>
            {translate('report.select_all', { _: 'Tümünü Seç' })}
          </Button>
          <Button size="small" onClick={clearAll}>
            {translate('report.clear_all', { _: 'Temizle' })}
          </Button>
        </Stack>

        <FormGroup>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.id}
              control={
                <Checkbox
                  checked={selected.includes(opt.id)}
                  onChange={() => toggle(opt.id)}
                  size="small"
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>

        {selected.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">
              {translate('report.selected_count', { count: selected.length, _: `Seçilen (${selected.length})` })}: {selectedLabels.join(' · ')}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          {translate('ra.action.cancel', { _: 'İptal' })}
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting || selected.length === 0}
          startIcon={exporting ? <CircularProgress size={16} /> : null}
        >
          {translate('report.download_excel', { _: 'Excel İndir' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleReportExportDialog;
